import axios from "axios";

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? "/api",
  timeout: 10000,
});

// Attach MCT JWT to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("mct_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-refresh: if backend sends a new token (token >50% through lifetime), store it silently.
// On 401 (expired / invalid token) clear stored token and signal the app to show login.
api.interceptors.response.use(
  (response) => {
    const newToken = response.headers["x-mct-new-token"];
    if (newToken) {
      localStorage.setItem("mct_token", newToken);
    }
    return response;
  },
  async (error) => {
    const isMctAuthRejection = error?.response?.headers?.["x-mct-auth"] === "required";
    if (error?.response?.status === 401 && isMctAuthRejection) {
      // Before showing login, try one silent refresh via /auth/status
      // The middleware auto-recovers expired tokens and sends X-MCT-New-Token
      try {
        const refreshResp = await axios.get(
          (import.meta.env.VITE_API_BASE_URL ?? "/api") + "/auth/status",
          { headers: { Authorization: `Bearer ${localStorage.getItem("mct_token")}` } }
        );
        const freshToken = refreshResp.headers["x-mct-new-token"];
        if (freshToken) {
          localStorage.setItem("mct_token", freshToken);
          // Retry the original request with new token
          error.config.headers.Authorization = `Bearer ${freshToken}`;
          return axios(error.config);
        }
      } catch {
        // Refresh also failed — truly expired
      }
      localStorage.removeItem("mct_token");
      window.dispatchEvent(new CustomEvent("mct:session-expired"));
    }
    return Promise.reject(error);
  },
);

export default api;

// Re-export everything from domain files for backwards compatibility
export * from './types';
export * from './portfolioApi';
export * from './signalsApi';
export * from './optionsApi';
export * from './adminApi';
